import type { MenuDTO } from "../dto/menu.dto";

type Mode = "LOCAL" | "DELIVERY";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

// single-flight caches (guardan Promises)
const menuPromiseCache = new Map<string, Promise<MenuDTO>>();
const featuresPromiseCache = new Map<string, Promise<FeatureDTO[]>>();
const visualPromiseCache = new Map<string, Promise<any[]>>();

export type FeatureDTO = { name: string; priority: number; items: string[] };

function baseUrl(restaurantId: string) {
  if (!SUPABASE_URL) throw new Error("VITE_SUPABASE_URL is required");
  return `${SUPABASE_URL}/storage/v1/object/public/menu-data/${restaurantId}`;
}



export async function getMenuDto(
  restaurantId: string,
  versionIso: string,
  mode: Mode,
  signal?: AbortSignal,
  opts?: { force?: boolean }
): Promise<MenuDTO> {
  const v = new Date(versionIso).getTime();
  const key = `${restaurantId}:${mode}:${v}`;

  if (!opts?.force) {
    const cached = menuPromiseCache.get(key);
    if (cached) return cached;
  }

  const modeFile = `${mode.toLowerCase()}.json`;

  const p = (async () => {
    const res = await fetch(`${baseUrl(restaurantId)}/${modeFile}?v=${v}`, {
      signal,
      cache: "no-store",
    });
    if (!res.ok) throw new Error("Menú no disponible");
    const dto = await res.json() as MenuDTO;
   // Guard mínimo — si la EF hizo su trabajo esto nunca se dispara
   if (!dto?.categories || !Array.isArray(dto.categories)) {
    throw new Error("Menú con estructura inválida");
  }
  
  return dto;
  })();

  menuPromiseCache.set(key, p);

  try {
    return await p;
  } catch (e) {
    menuPromiseCache.delete(key);
    throw e;
  }
}

export async function getFeaturesDto(
  restaurantId: string,
  versionIso: string,
  signal?: AbortSignal,
  opts?: { force?: boolean }
): Promise<FeatureDTO[]> {
  const v = new Date(versionIso).getTime();
  const key = `${restaurantId}:features:${v}`;

  if (!opts?.force) {
    const cached = featuresPromiseCache.get(key);
    if (cached) return cached;
  }

  const p = (async () => {
    const res = await fetch(`${baseUrl(restaurantId)}/features.json?v=${v}`, {
      signal,
      cache: "no-store",
    });
    if (!res.ok) return [];
    const json = await res.json();
    return Array.isArray(json) ? (json as FeatureDTO[]) : [];
  })();

  featuresPromiseCache.set(key, p);

  try {
    return await p;
  } catch (e) {
    featuresPromiseCache.delete(key);
    throw e;
  }
}

export async function getVisualContentRaw(
  restaurantId: string,
  versionIso: string,
  signal?: AbortSignal,
  opts?: { force?: boolean }
): Promise<any[]> {
  const v = new Date(versionIso).getTime();
  const key = `${restaurantId}:visual:${v}`;

  if (!opts?.force) {
    const cached = visualPromiseCache.get(key);
    if (cached) return cached;
  }

  const p = (async () => {
    const res = await fetch(`${baseUrl(restaurantId)}/visual_content.json?v=${v}`, {
      signal,
      cache: "no-store",
    });
    if (!res.ok) return [];
    const json = await res.json();
    // guard: si no es array, no explotamos
    return Array.isArray(json) ? json : [];
  })();

  visualPromiseCache.set(key, p);

  try {
    return await p;
  } catch (e) {
    visualPromiseCache.delete(key);
    throw e;
  }
}

export function clearRestaurantCache(restaurantId: string) {
  for (const k of menuPromiseCache.keys()) {
    if (k.startsWith(restaurantId + ":")) menuPromiseCache.delete(k);
  }
  for (const k of featuresPromiseCache.keys()) {
    if (k.startsWith(restaurantId + ":")) featuresPromiseCache.delete(k);
  }
  for (const k of visualPromiseCache.keys()) {
    if (k.startsWith(restaurantId + ":")) visualPromiseCache.delete(k);
  }
}